/**
 * Review History Panel
 * Shows all past reviews for a domain pack with outcomes and details
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Clock,
  User,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileText,
  TrendingUp,
  Brain,
  Calendar,
} from 'lucide-react';
import { getDomainPackReviews } from '../../lib/api/domainPacksApi';

interface ReviewHistoryProps {
  domainPackId: string;
}

interface Review {
  review_id: string;
  review_mode: 'quick' | 'full' | 'trust_ai';
  reviewer_name: string;
  reviewer_email: string;
  review_status: 'in_progress' | 'completed';
  overall_outcome: 'pending' | 'approved' | 'flagged' | 'rejected';
  review_started_at: string;
  review_completed_at?: string;
  total_review_time_seconds?: number;
  overall_reviewer_score?: number;
  overall_ai_score?: number;
  quick_review_summary?: string;
  full_review_strengths?: string[];
  full_review_weaknesses?: string[];
  approved_for_publication?: boolean;
  trust_ai_auto_approved?: boolean;
}

const ReviewHistory: React.FC<ReviewHistoryProps> = ({ domainPackId }) => {
  const { data: reviewsData, isLoading } = useQuery({
    queryKey: ['domain-pack-reviews', domainPackId],
    queryFn: () => getDomainPackReviews(domainPackId),
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  const reviews: Review[] = reviewsData?.reviews || [];
  const totalReviews = reviewsData?.total_reviews || 0;

  // Sort reviews by completion time (most recent first)
  const sortedReviews = [...reviews].sort((a, b) => {
    const dateA = new Date(a.review_completed_at || a.review_started_at);
    const dateB = new Date(b.review_completed_at || b.review_started_at);
    return dateB.getTime() - dateA.getTime();
  });

  const getReviewModeIcon = (mode: string) => {
    switch (mode) {
      case 'quick':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'full':
        return <FileText className="w-5 h-5 text-purple-600" />;
      case 'trust_ai':
        return <Brain className="w-5 h-5 text-green-600" />;
      default:
        return <FileText className="w-5 h-5 text-neutral-600" />;
    }
  };

  const getReviewModeName = (mode: string) => {
    switch (mode) {
      case 'quick':
        return 'Quick Review';
      case 'full':
        return 'Full Review';
      case 'trust_ai':
        return 'Trust AI';
      default:
        return 'Review';
    }
  };

  const getOutcomeIcon = (outcome: string) => {
    switch (outcome) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'flagged':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-neutral-400" />;
    }
  };

  const getOutcomeLabel = (outcome: string) => {
    switch (outcome) {
      case 'approved':
        return 'Approved';
      case 'flagged':
        return 'Flagged';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Pending';
    }
  };

  const getOutcomeColor = (outcome: string) => {
    switch (outcome) {
      case 'approved':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'flagged':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'rejected':
        return 'text-red-700 bg-red-50 border-red-200';
      default:
        return 'text-neutral-700 bg-neutral-50 border-neutral-200';
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes === 0) return `${remainingSeconds}s`;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">
          Review History
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-neutral-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Review History
          </h3>
          <span className="text-sm text-neutral-600">
            {totalReviews} review{totalReviews !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Reviews List */}
      <div className="divide-y divide-neutral-200">
        {sortedReviews.length === 0 ? (
          <div className="px-6 py-8 text-center text-neutral-600">
            <FileText className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
            <p className="text-sm">No reviews yet</p>
          </div>
        ) : (
          sortedReviews.map((review, index) => (
            <div key={review.review_id} className="px-6 py-4 hover:bg-neutral-50">
              {/* Review Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {getReviewModeIcon(review.review_mode)}
                  <div>
                    <div className="font-medium text-neutral-900">
                      Review #{sortedReviews.length - index} - {getReviewModeName(review.review_mode)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-neutral-600 mt-1">
                      <User className="w-3.5 h-3.5" />
                      <span>
                        {review.reviewer_name}{' '}
                        <span className="text-neutral-500">
                          ({review.reviewer_email})
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-medium ${getOutcomeColor(
                      review.overall_outcome
                    )}`}
                  >
                    {getOutcomeIcon(review.overall_outcome)}
                    {getOutcomeLabel(review.overall_outcome)}
                  </div>
                </div>
              </div>

              {/* Review Details */}
              <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                <div className="flex items-center gap-2 text-neutral-600">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {review.review_completed_at
                      ? formatDateTime(review.review_completed_at)
                      : 'In Progress'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-neutral-600">
                  <Clock className="w-4 h-4" />
                  <span>
                    Duration: {formatDuration(review.total_review_time_seconds)}
                  </span>
                </div>
              </div>

              {/* Mode-Specific Details */}
              {review.review_mode === 'full' &&
                review.overall_reviewer_score !== undefined && (
                  <div className="bg-neutral-50 rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-4 text-sm">
                      <div>
                        <span className="text-neutral-600">Reviewer Score:</span>
                        <span className="ml-2 font-semibold text-neutral-900">
                          {review.overall_reviewer_score}/100
                        </span>
                      </div>
                      <div>
                        <span className="text-neutral-600">AI Score:</span>
                        <span className="ml-2 font-semibold text-neutral-900">
                          {review.overall_ai_score}/100
                        </span>
                      </div>
                      {review.overall_reviewer_score &&
                        review.overall_ai_score &&
                        Math.abs(
                          review.overall_reviewer_score - review.overall_ai_score
                        ) <= 10 && (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs">Score Agreement</span>
                          </div>
                        )}
                    </div>
                  </div>
                )}

              {review.review_mode === 'trust_ai' && review.trust_ai_auto_approved && (
                <div className="bg-green-50 rounded-lg p-3 mb-3 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700">
                    Auto-approved by Trust AI (Score: {review.overall_ai_score}/100)
                  </span>
                </div>
              )}

              {/* Notes/Summary */}
              {review.quick_review_summary && (
                <div className="text-sm text-neutral-700 mt-2">
                  <span className="font-medium">Notes:</span> "
                  {review.quick_review_summary}"
                </div>
              )}

              {review.full_review_strengths &&
                review.full_review_strengths.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-neutral-700 mb-1">
                      Key Strengths:
                    </div>
                    <ul className="text-sm text-neutral-600 space-y-1">
                      {review.full_review_strengths.slice(0, 2).map((strength, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-green-600">+</span>
                          <span>{strength}</span>
                        </li>
                      ))}
                      {review.full_review_strengths.length > 2 && (
                        <li className="text-neutral-500 text-xs ml-4">
                          +{review.full_review_strengths.length - 2} more...
                        </li>
                      )}
                    </ul>
                  </div>
                )}

              {review.full_review_weaknesses &&
                review.full_review_weaknesses.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-neutral-700 mb-1">
                      Key Weaknesses:
                    </div>
                    <ul className="text-sm text-neutral-600 space-y-1">
                      {review.full_review_weaknesses.slice(0, 2).map((weakness, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-red-600">-</span>
                          <span>{weakness}</span>
                        </li>
                      ))}
                      {review.full_review_weaknesses.length > 2 && (
                        <li className="text-neutral-500 text-xs ml-4">
                          +{review.full_review_weaknesses.length - 2} more...
                        </li>
                      )}
                    </ul>
                  </div>
                )}

              {/* Publication Status */}
              {review.approved_for_publication && (
                <div className="mt-3 flex items-center gap-2 text-sm text-green-700">
                  <TrendingUp className="w-4 h-4" />
                  <span className="font-medium">Approved for Publication</span>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ReviewHistory;
